/**
 * NavigationTracker — wraps a Playwright Page to track navigation events.
 *
 * Intercepts page methods (goto, goBack, goForward, reload) and listens
 * for DOM events (framenavigated, domcontentloaded, load) plus injected
 * SPA detection scripts.
 */

import type { CapturedNetworkRequest, ConsoleLogEntry, ConsoleLogLevel, NavigationAnnotation, NavigationType, NetworkStats, ResourceBreakdown, ResourceType } from '@testrelic/core';
import { JsonlFileWriter } from './jsonl-stream.js';

// Minimal Playwright Page type subset for loose coupling
interface PageLike {
  goto(url: string, options?: unknown): Promise<unknown>;
  goBack(options?: unknown): Promise<unknown>;
  goForward(options?: unknown): Promise<unknown>;
  reload(options?: unknown): Promise<unknown>;
  url(): string;
  on(event: string, handler: (...args: unknown[]) => void): void;
  off(event: string, handler: (...args: unknown[]) => void): void;
  addInitScript(script: string | { path?: string } | (() => void)): Promise<void>;
  evaluate(fn: (...args: unknown[]) => unknown, ...args: unknown[]): Promise<unknown>;
  mainFrame(): { url(): string };
}

interface TestInfoLike {
  annotations: Array<{ type: string; description?: string }>;
}

interface NavigationRecord {
  url: string;
  navigationType: NavigationType;
  timestamp: string;
  domContentLoadedAt?: string;
  networkIdleAt?: string;
  networkStats?: NetworkStats;
}

interface NetworkCounter {
  totalRequests: number;
  failedRequests: number;
  failedRequestUrls: string[];
  totalBytes: number;
  byType: Record<keyof ResourceBreakdown, number>;
}

interface PendingRequest {
  url: string;
  method: string;
  resourceType: ResourceType;
  headers: Record<string, string>;
  postData: string | null;
  postDataTruncated: boolean;
  startedAt: string;
  startTimeMs: number;
  /** Index into `records`/`navCounters` of the navigation active when this
   *  request STARTED — so its response/failure stats attribute to the right
   *  navigation even if they arrive after the next navigation began. */
  navIndex: number;
}

const TEXT_CONTENT_TYPES = [
  'text/',
  'application/json',
  'application/xml',
  'application/javascript',
  'application/x-www-form-urlencoded',
  'application/graphql',
];

function isTextContentType(contentType: string): boolean {
  const lower = contentType.toLowerCase();
  return TEXT_CONTENT_TYPES.some(prefix => lower.includes(prefix));
}

export class NavigationTracker {
  private records: NavigationRecord[] = [];
  private listeners: Array<{ event: string; handler: (...args: unknown[]) => void }> = [];
  private origGoto: PageLike['goto'];
  private origGoBack: PageLike['goBack'];
  private origGoForward: PageLike['goForward'];
  private origReload: PageLike['reload'];
  private lastDomContentLoaded: string | undefined;
  private includeNetworkStats: boolean;
  /** One counter per navigation (parallel to `records` by index). A request's
   *  stats land in the counter of the nav that issued it, so a response arriving
   *  after the next navigation no longer leaks into it. */
  private navCounters: NetworkCounter[] = [];
  private pendingRequests = new Map<string, PendingRequest>();
  private networkWriter: JsonlFileWriter | null = null;
  private consoleWriter: JsonlFileWriter | null = null;
  private pendingBodyReads: Promise<void>[] = [];
  private requestIdCounter = 0;
  private networkRequestCount = 0;
  private consoleLogCount = 0;

  constructor(private page: PageLike, options?: { includeNetworkStats?: boolean }) {
    this.includeNetworkStats = options?.includeNetworkStats ?? true;
    // Store originals
    this.origGoto = page.goto.bind(page);
    this.origGoBack = page.goBack.bind(page);
    this.origGoForward = page.goForward.bind(page);
    this.origReload = page.reload.bind(page);

    // Initialize JSONL writers for heavy data
    try {
      if (this.includeNetworkStats) {
        this.networkWriter = new JsonlFileWriter('network');
      }
      this.consoleWriter = new JsonlFileWriter('console');
    } catch {
      // Graceful degradation if temp dir is not writable
    }

    this.interceptMethods();
    this.attachListeners();
  }

  async init(): Promise<void> {
    await this.injectSPADetection();
  }

  /** Flush remaining pending requests to the JSONL writer and close it. */
  async finalizeCapturedRequests(): Promise<void> {
    // Wait for all pending body reads to complete
    await Promise.allSettled(this.pendingBodyReads);
    this.pendingBodyReads = [];
    // Convert any remaining pending requests (incomplete) to JSONL entries
    for (const [, pending] of this.pendingRequests) {
      if (this.networkWriter) {
        this.networkWriter.append({
          url: pending.url,
          method: pending.method,
          resourceType: pending.resourceType,
          statusCode: 0,
          responseTimeMs: Date.now() - pending.startTimeMs,
          startedAt: pending.startedAt,
          requestHeaders: pending.headers,
          requestBody: pending.postData,
          responseBody: null,
          responseHeaders: null,
          contentType: null,
          responseSize: 0,
          requestBodyTruncated: pending.postDataTruncated,
          responseBodyTruncated: false,
          isBinary: false,
          error: 'incomplete',
        } satisfies CapturedNetworkRequest);
        this.networkRequestCount++;
      }
    }
    this.pendingRequests.clear();
    // Close writers so files are ready to be moved
    this.networkWriter?.close();
    this.consoleWriter?.close();
  }

  private static mapConsoleType(type: string): ConsoleLogLevel {
    switch (type) {
      case 'log': return 'log';
      case 'warning': return 'warn';
      case 'error': return 'error';
      case 'info': return 'info';
      case 'debug': return 'debug';
      default: return 'log';
    }
  }

  async getFileData(): Promise<{ navigations: NavigationAnnotation[]; networkRequestsFile: string | null; networkRequestsCount: number; consoleLogsFile: string | null; consoleLogsCount: number }> {
    // Assemble each navigation's network stats from its OWN counter, so every
    // navigation (not just the last) reflects exactly the requests it issued —
    // including responses that arrived after the next navigation started.
    this.assembleNetworkStats();

    const navigations: NavigationAnnotation[] = this.records.map(record => ({
      url: record.url,
      navigationType: record.navigationType,
      timestamp: record.timestamp,
      domContentLoadedAt: record.domContentLoadedAt,
      networkIdleAt: record.networkIdleAt,
      networkStats: record.networkStats,
    }));

    // Finalize and close JSONL writers
    if (this.includeNetworkStats) {
      await this.finalizeCapturedRequests();
    } else {
      this.consoleWriter?.close();
    }

    return {
      navigations,
      networkRequestsFile: this.networkWriter?.getCount() ? this.networkWriter.getPath() : null,
      networkRequestsCount: this.networkWriter?.getCount() ?? 0,
      consoleLogsFile: this.consoleWriter?.getCount() ? this.consoleWriter.getPath() : null,
      consoleLogsCount: this.consoleWriter?.getCount() ?? 0,
    };
  }

  /** @deprecated Legacy annotations no longer used — data flows via file-based payloads. */
  async flushLegacyAnnotations(_testInfo: TestInfoLike): Promise<void> {
    // No-op in v2 — all data flows via getFileData() and JSONL files
  }

  dispose(): void {
    // Restore original methods
    const patchable = NavigationTracker.asPatchable(this.page);
    patchable.goto = this.origGoto;
    patchable.goBack = this.origGoBack;
    patchable.goForward = this.origGoForward;
    patchable.reload = this.origReload;

    // Remove event listeners
    for (const { event, handler } of this.listeners) {
      this.page.off(event, handler);
    }
    this.listeners = [];
    this.records = [];
    this.pendingRequests.clear();
    this.pendingBodyReads = [];
    // Close writers (files kept for reporter to consume)
    this.networkWriter?.close();
    this.consoleWriter?.close();
  }

  getRecords(): readonly NavigationRecord[] {
    // Assemble on demand so each record reflects the latest per-nav counters
    // (late responses keep accumulating into the issuing nav's counter).
    this.assembleNetworkStats();
    return this.records;
  }

  /**
   * Treat the page as an indexable object so a method can be replaced.
   *
   * SAFETY: navigation capture swaps four methods on a live Playwright `Page`
   * and restores them in `dispose`. `Page` has no index signature, so the
   * write needs a widening step, and TypeScript prescribes routing a
   * conversion between non-overlapping types through `unknown`. The keys are
   * literals below, never user input.
   */
  private static asPatchable(page: PageLike): Record<string, unknown> {
    return page as unknown as Record<string, unknown>;
  }

  private interceptMethods(): void {
    const self = this;
    const page = this.page;

    const patchable = NavigationTracker.asPatchable(page);

    patchable.goto = async function (url: string, options?: unknown) {
      self.recordNavigation(url, 'goto');
      return self.origGoto(url, options);
    };

    patchable.goBack = async function (options?: unknown) {
      const result = await self.origGoBack(options);
      self.recordNavigation(page.url(), 'back');
      return result;
    };

    patchable.goForward = async function (options?: unknown) {
      const result = await self.origGoForward(options);
      self.recordNavigation(page.url(), 'forward');
      return result;
    };

    patchable.reload = async function (options?: unknown) {
      self.recordNavigation(page.url(), 'refresh');
      return self.origReload(options);
    };
  }

  private attachListeners(): void {
    // Track DOMContentLoaded for lifecycle timestamps
    const onDomContentLoaded = () => {
      this.lastDomContentLoaded = new Date().toISOString();
      if (this.records.length > 0) {
        this.records[this.records.length - 1].domContentLoadedAt = this.lastDomContentLoaded;
      }
    };
    this.page.on('domcontentloaded', onDomContentLoaded as (...args: unknown[]) => void);
    this.listeners.push({ event: 'domcontentloaded', handler: onDomContentLoaded as (...args: unknown[]) => void });

    // Track main frame navigation for non-intercepted navigations (link clicks, form submits)
    const onFrameNavigated = (frame: unknown) => {
      // Only track main frame
      try {
        const frameObj = frame as { url(): string; parentFrame(): unknown };
        if (typeof frameObj.parentFrame === 'function' && frameObj.parentFrame() !== null) {
          return; // Skip sub-frames
        }
        const url = frameObj.url();
        // Skip if we already recorded this URL in the last 50ms (from method interception)
        const lastRecord = this.records[this.records.length - 1];
        if (lastRecord) {
          const timeDiff = Date.now() - new Date(lastRecord.timestamp).getTime();
          if (timeDiff < 50 && lastRecord.url === url) {
            return; // Duplicate from method interception
          }
        }
        // This is an untracked navigation (link click, form submit, etc.)
        this.recordNavigation(url, 'navigation');
      } catch {
        // Ignore frame navigation errors
      }
    };
    this.page.on('framenavigated', onFrameNavigated);
    this.listeners.push({ event: 'framenavigated', handler: onFrameNavigated });

    // Track console messages: SPA detection + full console log capture
    const onConsoleMessage = (msg: unknown) => {
      try {
        const msgObj = msg as { text(): string; type(): string; location(): { url: string; lineNumber: number; columnNumber: number } };
        const type = msgObj.type();
        const text = msgObj.text();

        // Internal SPA navigation detection via __testrelic_nav: debug messages
        if (type === 'debug' && text.startsWith('__testrelic_nav:')) {
          try {
            const data = JSON.parse(text.slice('__testrelic_nav:'.length));
            if (data.type && data.url) {
              this.recordNavigation(data.url, data.type as NavigationType);
            }
          } catch {
            // Ignore parse errors
          }
          return;
        }

        // Capture all console messages
        {
          let location: string | null = null;
          try {
            const loc = msgObj.location();
            if (loc && loc.url) {
              location = `${loc.url}:${loc.lineNumber}:${loc.columnNumber}`;
            }
          } catch {
            // location() may not be available
          }
          const level = NavigationTracker.mapConsoleType(type);
          if (this.consoleWriter) {
            this.consoleWriter.append({ level, text, timestamp: new Date().toISOString(), location } satisfies ConsoleLogEntry);
            this.consoleLogCount++;
          }
        }
      } catch {
        // Ignore console capture errors
      }
    };
    this.page.on('console', onConsoleMessage);
    this.listeners.push({ event: 'console', handler: onConsoleMessage });

    // Network stats tracking + individual request capture
    if (this.includeNetworkStats) {
      const onRequest = (request: unknown) => {
        // Attribute this request to the navigation active right now; its response
        // may arrive after the next navigation starts, but the count belongs here.
        const navIndex = this.records.length - 1;
        const counter = this.counterForNav(navIndex);
        if (counter) {
          counter.totalRequests++;
        }
        // Capture individual request details
        try {
          const req = request as {
            url(): string;
            method(): string;
            resourceType(): string;
            headers(): Record<string, string>;
            postData(): string | null;
          };
          const reqId = String(this.requestIdCounter++);
          const postData = req.postData() ?? null;
          const pending: PendingRequest = {
            url: req.url(),
            method: req.method(),
            resourceType: this.mapResourceType(req.resourceType()),
            headers: req.headers(),
            postData,
            postDataTruncated: false,
            startedAt: new Date().toISOString(),
            startTimeMs: Date.now(),
            navIndex,
          };
          this.pendingRequests.set(reqId, pending);
          // Tag the request object with our ID for lookup in response/failed handlers
          (request as Record<string, unknown>).__testrelic_id = reqId;
        } catch {
          // Ignore request capture errors
        }
      };
      this.page.on('request', onRequest);
      this.listeners.push({ event: 'request', handler: onRequest });

      const onResponse = (response: unknown) => {
        try {
          const resp = response as {
            url(): string;
            status(): number;
            headers(): Record<string, string>;
            request(): { resourceType(): string; __testrelic_id?: string };
            body(): Promise<Buffer>;
          };
          // Attribute aggregate stats to the navigation that ISSUED the request
          // (falls back to the current nav when the request wasn't tracked).
          const reqId = (resp.request() as Record<string, unknown>).__testrelic_id as string | undefined;
          const pending = reqId ? this.pendingRequests.get(reqId) : undefined;
          const counter = this.counterForNav(pending ? pending.navIndex : this.records.length - 1);
          if (counter) {
            const status = resp.status();
            if (status >= 400) {
              counter.failedRequests++;
              counter.failedRequestUrls.push(status + ' ' + resp.url());
            }
            const contentLength = resp.headers()['content-length'];
            if (contentLength) {
              counter.totalBytes += parseInt(contentLength, 10) || 0;
            }
            const resourceType = resp.request().resourceType();
            const typeKey = this.mapResourceType(resourceType);
            counter.byType[typeKey]++;
          }
          // Individual request capture
          if (!reqId || !pending) return;
          this.pendingRequests.delete(reqId);
          const responseTimeMs = Date.now() - pending.startTimeMs;
          const respHeaders = resp.headers();
          const contentType = respHeaders['content-type'] ?? null;
          const responseSize = parseInt(respHeaders['content-length'] ?? '0', 10) || 0;
          const binary = contentType ? !isTextContentType(contentType) : false;
          // Read response body asynchronously and write to JSONL (not held in memory)
          const bodyPromise = (async () => {
            let responseBody: string | null = null;
            if (!binary) {
              try {
                const buf = await resp.body();
                responseBody = buf.toString('utf-8');
              } catch {
                // Body unavailable (e.g., redirect)
              }
            }
            const captured: CapturedNetworkRequest = {
              url: pending.url,
              method: pending.method,
              resourceType: pending.resourceType,
              statusCode: resp.status(),
              responseTimeMs,
              startedAt: pending.startedAt,
              requestHeaders: pending.headers,
              requestBody: pending.postData,
              responseBody,
              responseHeaders: respHeaders,
              contentType,
              responseSize,
              requestBodyTruncated: pending.postDataTruncated,
              responseBodyTruncated: false,
              isBinary: binary,
              error: null,
            };
            // Write to JSONL file — body is released from memory after this
            if (this.networkWriter) {
              this.networkWriter.append(captured);
              this.networkRequestCount++;
            }
          })();
          this.pendingBodyReads.push(bodyPromise);
        } catch {
          // Ignore response processing errors
        }
      };
      this.page.on('response', onResponse);
      this.listeners.push({ event: 'response', handler: onResponse });

      const onRequestFailed = (request: unknown) => {
        const reqId = (request as Record<string, unknown>).__testrelic_id as string | undefined;
        const pending = reqId ? this.pendingRequests.get(reqId) : undefined;
        // Attribute the failure to the navigation that issued the request.
        const counter = this.counterForNav(pending ? pending.navIndex : this.records.length - 1);
        if (counter) {
          counter.failedRequests++;
          try {
            const req = request as { url(): string };
            counter.failedRequestUrls.push('ERR ' + req.url());
          } catch {
            // Ignore
          }
        }
        // Individual request capture
        try {
          const req = request as { failure(): { errorText: string } | null };
          if (!reqId || !pending) return;
          this.pendingRequests.delete(reqId);
          const captured: CapturedNetworkRequest = {
            url: pending.url,
            method: pending.method,
            resourceType: pending.resourceType,
            statusCode: 0,
            responseTimeMs: Date.now() - pending.startTimeMs,
            startedAt: pending.startedAt,
            requestHeaders: pending.headers,
            requestBody: pending.postData,
            responseBody: null,
            responseHeaders: null,
            contentType: null,
            responseSize: 0,
            requestBodyTruncated: pending.postDataTruncated,
            responseBodyTruncated: false,
            isBinary: false,
            error: req.failure()?.errorText ?? 'Unknown error',
          };
          if (this.networkWriter) {
            this.networkWriter.append(captured);
            this.networkRequestCount++;
          }
        } catch {
          // Ignore
        }
      };
      this.page.on('requestfailed', onRequestFailed);
      this.listeners.push({ event: 'requestfailed', handler: onRequestFailed });
    }
  }

  private async injectSPADetection(): Promise<void> {
    try {
      await this.page.addInitScript(() => {
        const origPush = history.pushState.bind(history);
        const origReplace = history.replaceState.bind(history);

        history.pushState = function (...args: Parameters<typeof origPush>) {
          origPush(...args);
          // eslint-disable-next-line no-console
          console.debug('__testrelic_nav:' + JSON.stringify({
            type: 'spa_route',
            url: location.href,
          }));
        };

        history.replaceState = function (...args: Parameters<typeof origReplace>) {
          origReplace(...args);
          // eslint-disable-next-line no-console
          console.debug('__testrelic_nav:' + JSON.stringify({
            type: 'spa_replace',
            url: location.href,
          }));
        };

        window.addEventListener('popstate', () => {
          // eslint-disable-next-line no-console
          console.debug('__testrelic_nav:' + JSON.stringify({
            type: 'popstate',
            url: location.href,
          }));
        });

        window.addEventListener('hashchange', () => {
          // eslint-disable-next-line no-console
          console.debug('__testrelic_nav:' + JSON.stringify({
            type: 'hash_change',
            url: location.href,
          }));
        });
      });
    } catch {
      // Ignore injection errors (page may be closed)
    }
  }

  private recordNavigation(url: string, type: NavigationType): void {
    this.records.push({
      url,
      navigationType: type,
      timestamp: new Date().toISOString(),
    });

    // Each navigation gets its own counter at the same index as its record, so
    // late-arriving responses/failures attribute to the nav that issued the
    // request (via PendingRequest.navIndex) instead of leaking to the next nav.
    if (this.includeNetworkStats) {
      this.navCounters.push(this.createNetworkCounter());
    }
  }

  private createNetworkCounter(): NetworkCounter {
    return {
      totalRequests: 0,
      failedRequests: 0,
      failedRequestUrls: [],
      totalBytes: 0,
      byType: { xhr: 0, document: 0, script: 0, stylesheet: 0, image: 0, font: 0, other: 0 },
    };
  }

  /** The per-navigation counter for a record index, or undefined when the index
   *  predates the first navigation (-1) or is out of range. */
  private counterForNav(index: number): NetworkCounter | undefined {
    return index >= 0 ? this.navCounters[index] : undefined;
  }

  /** Snapshot each navigation's accumulated counter into record.networkStats.
   *  Idempotent — safe to call repeatedly (getRecords / getFileData). */
  private assembleNetworkStats(): void {
    if (!this.includeNetworkStats) return;
    for (let i = 0; i < this.records.length; i++) {
      const counter = this.navCounters[i];
      if (!counter) continue;
      this.records[i].networkStats = {
        totalRequests: counter.totalRequests,
        failedRequests: counter.failedRequests,
        failedRequestUrls: [...counter.failedRequestUrls],
        totalBytes: counter.totalBytes,
        byType: { ...counter.byType },
      };
    }
  }

  private mapResourceType(type: string): keyof ResourceBreakdown {
    switch (type) {
      case 'xhr':
      case 'fetch':
        return 'xhr';
      case 'document':
        return 'document';
      case 'script':
        return 'script';
      case 'stylesheet':
        return 'stylesheet';
      case 'image':
        return 'image';
      case 'font':
        return 'font';
      default:
        return 'other';
    }
  }
}
